/**
 * In-shell login popup near the FAB (DOCK-12).
 *
 * Two-phase UX:
 *   1. `open()` — Dock-branded logged-out card (title / subtitle / Log in)
 *   2. `startLogin()` — mounts the SO → Auth0 iframe at LOGIN_POPUP_URL
 *
 * Login success is detected by the hellopearl-bridge content script running
 * inside the popup's post-login page: it finds the Auth0 token in
 * localStorage and sends `PEARL_AUTH_TOKEN_FOUND` to the background, which
 * forwards `PEARL_LOGIN_DETECTED` (with the token) to the content script.
 * That closes this popup, transitions the shell to connected, and starts
 * iframe preloading.
 *
 * The iframe deliberately does NOT carry a `sandbox` attribute. Auth flows
 * (cross-origin redirects to `*.auth0.com`, top-level navigation, popups,
 * social-login buttons) frequently fail under sandbox restrictions even
 * with `allow-same-origin allow-scripts allow-forms` because each provider
 * adds its own constraint. The product surface area here is small (a single
 * popup that closes on success) so leaving it un-sandboxed is the simpler,
 * more robust choice. The iframe is still gated to hellopearl.com via the
 * URL we load.
 *
 * The CSS lives in `styles/login.css.ts` under `.pw-login-popup`.
 */

import { DOCK_LOGGED_OUT_COPY } from '../popup/popup-auth-view';
import { LOGIN_BASE } from './url-builder';

/** Default URL that the login host redirects to from when unauthenticated. */
export const LOGIN_POPUP_URL = `${LOGIN_BASE}/`;

/** Small × icon used by the dismiss button in the popup's top-right corner. */
const CLOSE_X_SVG =
  '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M5.25 5.25L12 12M12 12L5.25 18.75M12 12L18.75 18.75M12 12L18.75 5.25" stroke="#050505" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

export interface LoginPopupCallbacks {
  /** Fired when the user explicitly dismisses the popup (close button). */
  onDismiss?: () => void;
  /** Fired when the user clicks Log in / startLogin (clear signed-out latch). */
  onStartLogin?: () => void;
}

export interface LoginPopup {
  /** Show the Dock logged-out card (no Auth0 iframe yet). */
  open: () => void;
  /** Mount the SO/Auth0 iframe and notify onStartLogin. */
  startLogin: () => void;
  /** Hide the popup; tear down card + iframe so the next open is clean. */
  close: () => void;
  /** Whether the popup is currently visible. */
  isOpen: () => boolean;
  /** Remove the popup element from the DOM (idempotent). */
  destroy: () => void;
  /**
   * Test/debug accessor — exposes the underlying popup element for shell tests.
   * Production code should not depend on this.
   */
  getElement: () => HTMLElement;
}

type ShadowLike = ShadowRoot | HTMLElement;

/**
 * Creates a login popup attached to the given shadow root.
 *
 * @param doc - Owning document (the host page's document).
 * @param shadow - Where the popup element is appended.
 * @param callbacks - Optional dismiss / start-login hooks.
 * @returns A handle for opening, starting login, closing, and teardown.
 */
export function createLoginPopup(
  doc: Document,
  shadow: ShadowLike,
  callbacks: LoginPopupCallbacks = {},
): LoginPopup {
  const popup = doc.createElement('div');
  popup.className = 'pw-login-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-label', 'Pearl login');

  const closeBtn = doc.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'pw-login-popup-close';
  closeBtn.setAttribute('aria-label', 'Close login');
  closeBtn.innerHTML = CLOSE_X_SVG;

  const card = doc.createElement('div');
  card.className = 'pw-login-card';
  card.setAttribute('data-testid', 'dock-logged-out-card');

  const title = doc.createElement('h2');
  title.className = 'pw-login-card__title';
  title.textContent = DOCK_LOGGED_OUT_COPY.title;

  const subtitle = doc.createElement('p');
  subtitle.className = 'pw-login-card__subtitle';
  subtitle.textContent = DOCK_LOGGED_OUT_COPY.subtitle;

  const loginBtn = doc.createElement('button');
  loginBtn.type = 'button';
  loginBtn.className = 'pw-login-card__cta';
  loginBtn.textContent = DOCK_LOGGED_OUT_COPY.cta;

  card.appendChild(title);
  card.appendChild(subtitle);
  card.appendChild(loginBtn);

  popup.appendChild(closeBtn);
  popup.appendChild(card);
  shadow.appendChild(popup);

  let iframe: HTMLIFrameElement | null = null;
  let openState = false;

  function showCard(): void {
    card.hidden = false;
  }

  function hideCard(): void {
    card.hidden = true;
  }

  function mountIframe(): void {
    if (iframe) return;
    hideCard();
    iframe = doc.createElement('iframe');
    iframe.src = LOGIN_POPUP_URL;
    iframe.setAttribute('aria-label', 'Pearl login');
    iframe.setAttribute(
      'allow',
      'clipboard-write; web-share; identity-credentials-get *',
    );
    popup.appendChild(iframe);
  }

  function open(): void {
    if (openState) return;
    openState = true;
    showCard();
    popup.classList.add('visible');
  }

  function startLogin(): void {
    if (!openState) {
      openState = true;
      popup.classList.add('visible');
    }
    if (callbacks.onStartLogin) callbacks.onStartLogin();
    mountIframe();
  }

  function close(): void {
    if (!openState) return;
    openState = false;
    popup.classList.remove('visible');
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
    iframe = null;
    showCard();
  }

  closeBtn.addEventListener('click', ev => {
    ev.stopPropagation();
    close();
    if (callbacks.onDismiss) callbacks.onDismiss();
  });

  loginBtn.addEventListener('click', ev => {
    ev.stopPropagation();
    startLogin();
  });

  function destroy(): void {
    close();
    if (popup.parentNode) popup.parentNode.removeChild(popup);
  }

  return {
    open,
    startLogin,
    close,
    isOpen: () => openState,
    destroy,
    getElement: () => popup,
  };
}
